import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkAvailableSlot,
  SdkAvailableSlotsHandle,
  SdkAvailableSlotsOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { schedulingRequestDatetime } from './schedulingRequestDatetime';

type AvailableSlotsResponse = {
  slots: SdkAvailableSlot[];
  timezone: string;
  stopped_early: boolean;
};

const NO_SLOTS: SdkAvailableSlot[] = [];

function isCompleteSearch(options: SdkAvailableSlotsOptions, from: string, to: string): boolean {
  return (
    options.resourceId.length > 0 &&
    options.groups.length > 0 &&
    options.groups.every((group) => group.records.length > 0) &&
    from !== '' &&
    to !== '' &&
    Number.isFinite(options.durationMinutes) &&
    options.durationMinutes > 0
  );
}

/** The request body in the shape the endpoint reads. Omitted options are not sent. */
function slotSearchBody(options: SdkAvailableSlotsOptions, from: string, to: string) {
  return {
    groups: options.groups.map((group) => ({
      ...(group.mode !== undefined ? { mode: group.mode } : {}),
      ...(group.strategy !== undefined ? { strategy: group.strategy } : {}),
      records: group.records.map((record) => ({ resource_id: record.resourceId, id: record.id }))
    })),
    from,
    to,
    duration_minutes: options.durationMinutes,
    ...(options.stepMinutes !== undefined ? { step_minutes: options.stepMinutes } : {}),
    ...(options.noticeMinutes !== undefined ? { notice_minutes: options.noticeMinutes } : {}),
    ...(options.maxAdvanceDays !== undefined ? { max_advance_days: options.maxAdvanceDays } : {}),
    ...(options.quantity !== undefined ? { quantity: options.quantity } : {}),
    ...(options.maxSlots !== undefined ? { max_slots: options.maxSlots } : {}),
    ...(options.timezone !== undefined ? { timezone: options.timezone } : {}),
    ...(options.seed !== undefined ? { seed: options.seed } : {})
  };
}

/** Runtime implementation of `Sdk.hooks.useAvailableSlots`. */
export function useAvailableSlots(options: SdkAvailableSlotsOptions): SdkAvailableSlotsHandle {
  const { resourceId, enabled = true } = options;
  const fromValue = schedulingRequestDatetime(options.from);
  const toValue = schedulingRequestDatetime(options.to);
  const canSearch = enabled && isCompleteSearch(options, fromValue, toValue);
  const body = slotSearchBody(options, fromValue, toValue);
  const query = useQuery<AvailableSlotsResponse, ReturnType<typeof toApiError>>({
    queryKey: [PAGE_API_QUERY_KEY_HEAD, 'resources', resourceId, 'slots', body],
    enabled: canSearch,
    retry: false,
    refetchOnMount: 'always',
    staleTime: 0,
    throwOnError: false,
    queryFn: async ({ signal }) =>
      createApi(axios).post<AvailableSlotsResponse>(
        `/api/data/resources/${encodeURIComponent(resourceId)}/slots`,
        body,
        { signal }
      )
  });
  // A refetch of the same inputs keeps the last result on screen; new inputs have no data yet.
  const isLoading = canSearch && query.data === undefined && (query.isFetching || query.isPaused);
  const result = canSearch && !query.isError ? query.data : undefined;

  return {
    slots: result?.slots ?? NO_SLOTS,
    timezone: result?.timezone ?? null,
    stoppedEarly: result?.stopped_early ?? false,
    isLoading,
    error: canSearch ? query.error : null,
    refetch: async () => {
      if (canSearch) {
        await query.refetch();
      }
    }
  };
}
